import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import BackofficeLayout from "@/components/layouts/BackofficeLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Search,
  Loader2,
  Eye,
  Download,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { orderApi, associationApi } from "@/services/api";
import { useToast } from "@/components/ui/use-toast";
import { Order, OrderStatus, Association, OrderWithRefunds } from "@/models";
import { extractDataArray } from "@/utils/apiUtils";
import { SUPABASE_URL, SUPABASE_ANON_KEY } from "@/utils/apiUtils";
import { customerTypeApi } from "@/services/api/customerApi";
import { objectsToCSV, downloadCSV } from "@/utils/csvExport";
import { useAuth } from "@/contexts/AuthContext";

interface OrderSearchParams {
  search?: string;
  association_id?: number;
  customer_type_id?: number;
  status?: OrderStatus | OrderStatus[];
  limit?: number;
  offset?: number;
  include_customer?: boolean;
  include_refunds?: boolean;
}

const getBadgeVariant = (status: string) => {
  switch (status) {
    case "approved":
      return "success";
    case "under_review":
    case "application_submitted":
      return "warning";
    case "pending_payment":
      return "secondary";
    case "draft":
      return "secondary";
    case "denied":
    case "rejected":
      return "destructive";
    default:
      return "outline";
  }
};

const getStatusText = (status: string) => {
  switch (status) {
    case "approved":
      return "Approved";
    case "under_review":
      return "Under Review";
    case "application_submitted":
      return "Submitted";
    case "pending_payment":
      return "Pending Payment";
    case "denied":
    case "rejected":
      return "Denied";
    default:
      return status.replace(/_/g, " ");
  }
};

const PAGE_SIZE = 10;

const DOCUMENT_TYPE_COLORS = [
  "#FF6633", "#FFB399", "#FF33FF", "#FFFF99", "#00B3E6",
  "#E6B333", "#3366E6", "#999966", "#99FF99", "#B34D4D",
  "#80B300", "#809900", "#E6B3B3", "#6680B3", "#66991A",
  "#FF99E6", "#CCFF1A", "#FF1A66", "#E6331A", "#33FFCC",
];

const Orders = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [associationId, setAssociationId] = useState<string>("all");
  const [customerTypeFilter, setCustomerTypeFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { userRoles, selectedRole, loading, permissionsLoaded } = useAuth();
  const [associationsMap, setAssociationsMap] = useState<Record<number, Association>>({});
  const [isExporting, setIsExporting] = useState(false);
  const [selectedTab, setSelectedTab] = useState<'all' | 'draft' | 'pending' | 'completed' | 'archived'>('all');

  // Compute status for API call based on selectedTab
  // Cancelled and rejected orders live only in the Archived tab, so "All" excludes them.
  // Listed explicitly: OrderStatus also holds "Pending" and "refunded", which are not
  // values of the order_status enum, and sending them makes the orders query fail.
  let statusForApi: OrderStatus | OrderStatus[] | undefined = undefined;
  if (selectedTab === 'all') statusForApi = [
    OrderStatus.draft,
    OrderStatus.application_submitted,
    OrderStatus.under_review,
    OrderStatus.on_hold,
    OrderStatus.approved
  ];
  else if (selectedTab === 'draft') statusForApi = OrderStatus.draft;
  else if (selectedTab === 'pending') statusForApi = [
    OrderStatus.application_submitted,
    OrderStatus.under_review,
    OrderStatus.on_hold
  ];
  else if (selectedTab === 'completed') statusForApi = [OrderStatus.approved];
  else if (selectedTab === 'archived') statusForApi = [
    OrderStatus.cancelled,
    OrderStatus.rejected
  ];

  // Get API filters based on selected role - simplified for backend filtering
  const getApiFilters = () => {
    const filters: any = {};
    
    // Backend will now handle role-based filtering automatically
    // No need to pass show_all, show_none, or management_company_id
    
    return filters;
  };

  const searchParams: OrderSearchParams = {
    search: searchTerm,
    association_id: associationId !== "all" ? parseInt(associationId) : undefined,
    customer_type_id: customerTypeFilter !== "all" ? parseInt(customerTypeFilter) : undefined,
    status: statusForApi,
    include_customer: true,
    limit: pageSize,
    offset: (currentPage - 1) * pageSize,
    include_refunds: true,
    ...getApiFilters()
  };

  const { data: ordersResponse, isLoading: isLoadingOrders } = useQuery({
    queryKey: ["orders", searchParams, selectedRole?.type, selectedRole?.entityId],
    queryFn: async () => orderApi.list(searchParams),
    enabled: permissionsLoaded && !!selectedRole,
  });

  // Only log when there's a meaningful change
  React.useEffect(() => {
    if (ordersResponse) {
      console.log("Orders Response:", ordersResponse);
    }
  }, [ordersResponse?.data?.length, ordersResponse?.total]);

  // Get associations filter for the associations dropdown - simplified for backend filtering
  const getAssociationsApiFilters = () => {
    const filters: any = { limit: 10000 };
    
    // Backend will now handle role-based filtering automatically
    // No need to pass show_all, show_none, or management_company_id
    
    return filters;
  };

  const { data: associationsResponse = [], isLoading: isLoadingAssociations } = useQuery({
    queryKey: ["all_associations", selectedRole?.type, selectedRole?.entityId],
    queryFn: async () => {
      const result = await associationApi.list({ 
        queryKey: ["all_associations", getAssociationsApiFilters()]
      });
      return result;
    },
    enabled: permissionsLoaded && !!selectedRole,
  });

  const { data: customerTypesResponse = [], isLoading: isLoadingCustomerTypes } = useQuery({
    queryKey: ["customer_types"],
    queryFn: async () => {
      return await customerTypeApi.list();
    },
    enabled: permissionsLoaded,
  });

  const orders = (ordersResponse?.data || []) as OrderWithRefunds[];
  const associations = extractDataArray(associationsResponse);
  
  useEffect(() => {
    if (associations.length > 0) {
      const map: Record<number, Association> = {};
      associations.forEach((association: Association) => {
        map[association.id] = association;
      });
      setAssociationsMap(map);
    }
  }, [associations]);

  // Reset current page when tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedTab]);

  // Reset current page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, associationId, customerTypeFilter]);

  const handleViewOrder = (orderId: number) => {
    navigate(`/backoffice/orders/${orderId}`);
  };

  const handleExportCSV = async () => {
    try {
      setIsExporting(true);
      
      // Create search params without pagination for export
      const exportSearchParams: OrderSearchParams = {
        search: searchTerm,
        association_id: associationId !== "all" ? parseInt(associationId) : undefined,
        customer_type_id: customerTypeFilter !== "all" ? parseInt(customerTypeFilter) : undefined,
        status: statusForApi,
        include_customer: true,
        limit: 10000, // Set a high limit to get all orders
        ...getApiFilters() // Use consistent role-based filtering
      };

      console.log('Export Search Params:', exportSearchParams);

      // Fetch all orders matching the current filters
      const exportResponse = await orderApi.list(exportSearchParams);
      console.log('Export API Response:', exportResponse);
      
      const allOrders = exportResponse.data || [];
      console.log('Number of orders fetched:', allOrders.length);
      console.log('First order sample:', allOrders[0]);
      
      // Format the orders data for CSV export
      const exportData = allOrders.map(order => ({
        'Order ID': order.id,
        'Customer Name': order.customer ? `${order.customer.first_name} ${order.customer.last_name}` : 'N/A',
        'Customer Email': order.customer?.email || 'N/A',
        'Customer Type': customerTypesResponse.find(type => type.id === order.customer_type_id)?.name || 'N/A',
        'Association': order.association?.name || `Association ID: ${order.association_id}`,
        'Status': getStatusText(order.status),
        'Expected Delivery Date': order.expected_delivery_date ? new Date(order.expected_delivery_date).toLocaleDateString() : 'Not set',
        'Total Amount': `$${Number(order.total_amount).toFixed(2)}`,
        'Processing Type': order.processing_type,
        'Created Date': new Date(order.created_at).toLocaleDateString(),
        'Updated Date': new Date(order.updated_at).toLocaleDateString(),
      }));

      console.log('Formatted export data sample:', exportData[0]);
      console.log('Total records to export:', exportData.length);

      // Generate and download the CSV
      const csvData = objectsToCSV(exportData);
      downloadCSV(csvData, `orders_export_${new Date().toISOString().split('T')[0]}.csv`);
      
      toast({
        title: "Export Successful",
        description: `${exportData.length} orders exported to CSV`,
      });
    } catch (error) {
      console.error("Error exporting orders to CSV:", error);
      toast({
        title: "Export Failed",
        description: "Could not export orders to CSV. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const statusOptions = [
    { value: "all", label: "All Statuses" },
    { value: OrderStatus.draft, label: "Draft" },
    { value: OrderStatus.application_submitted, label: "Submitted" },
    { value: OrderStatus.under_review, label: "Under Review" },
    { value: OrderStatus.on_hold, label: "On Hold" },
    { value: OrderStatus.approved, label: "Approved" },
    { value: OrderStatus.rejected, label: "Rejected" },
    { value: OrderStatus.cancelled, label: "Cancelled" }
  ];



  const getRemainingTime = (expectedDate: string) => {
    const now = new Date();
    const deliveryDate = new Date(expectedDate);
    const diffMs = deliveryDate.getTime() - now.getTime();
    
    // If date has passed, show negative time
    if (diffMs < 0) {
      const pastMs = Math.abs(diffMs);
      const pastDays = Math.floor(pastMs / (1000 * 60 * 60 * 24));
      const pastHours = Math.floor(pastMs / (1000 * 60 * 60));
      const pastMinutes = Math.floor(pastMs / (1000 * 60));
      
      if (pastDays > 0) {
        return { text: `${pastDays} day${pastDays > 1 ? 's' : ''} overdue`, isOverdue: true };
      } else if (pastHours > 0) {
        return { text: `${pastHours} hour${pastHours > 1 ? 's' : ''} overdue`, isOverdue: true };
      } else {
        return { text: `${pastMinutes} min${pastMinutes > 1 ? 's' : ''} overdue`, isOverdue: true };
      }
    }
    
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor(diffMs / (1000 * 60));
    
    if (days > 0) {
      return { text: `${days} day${days > 1 ? 's' : ''} left`, isOverdue: false };
    } else if (hours > 0) {
      return { text: `${hours} hour${hours > 1 ? 's' : ''} left`, isOverdue: false };
    } else if (minutes > 0) {
      return { text: `${minutes} min${minutes > 1 ? 's' : ''} left`, isOverdue: false };
    } else {
      return { text: 'Due now', isOverdue: false };
    }
  };

  const pendingStatuses = [
    OrderStatus.application_submitted,
    OrderStatus.under_review,
    OrderStatus.on_hold
  ];
  const draftStatuses = [OrderStatus.draft];
  const completedStatuses = [OrderStatus.approved];

  const ordersAll = orders;
  const ordersPending = orders.filter(
    (order) => pendingStatuses.includes(order.status)
  );
  const ordersDraft = orders.filter(
    (order) => draftStatuses.includes(order.status)
  );
  const ordersCompleted = orders.filter(
    (order) => completedStatuses.includes(order.status)
  );

  // Since the backend already handles search filtering, we don't need client-side filtering
  // The backend search is more comprehensive and includes associations, properties, etc.
  const filteredOrders = orders;

  // Only log filtered orders when there's a meaningful change
  React.useEffect(() => {
    if (filteredOrders.length > 0) {
      console.log("Filtered Orders:", filteredOrders);
    }
  }, [filteredOrders.length]);

  // Show loading state while permissions are loading
  if (loading || !permissionsLoaded) {
    return (
      <BackofficeLayout>
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-brand-blue" />
          <span className="ml-2">Loading permissions...</span>
        </div>
      </BackofficeLayout>
    );
  }

  // Show message if no role is selected
  if (!selectedRole) {
    return (
      <BackofficeLayout>
        <div className="text-center py-8">
          <h3 className="text-lg font-medium">No role selected</h3>
          <p className="text-sm text-muted-foreground">
            Please select a role to view orders.
          </p>
        </div>
      </BackofficeLayout>
    );
  }

  const OrdersTable = ({ ordersList }: { ordersList: OrderWithRefunds[] }) => (
    ordersList.length > 0 ? (
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order ID</TableHead>
              <TableHead>Expected date</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Association</TableHead>
              <TableHead>Documents</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Total Amount</TableHead>
              <TableHead>Created At</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ordersList.map((order: OrderWithRefunds) => {
              const totalRefunded = order.refunds?.reduce((sum, refund) => sum + refund.amount, 0) || 0;
              return (
                <TableRow key={order.id} className="cursor-pointer hover:bg-gray-50" onClick={() => handleViewOrder(order.id)}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <span>{order.id}</span>
                      {order.parent_id && (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          Update of #{order.parent_id}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {order.expected_delivery_date ? (() => {
                      const remaining = getRemainingTime(order.expected_delivery_date);
                      return (
                        <div className="text-sm">
                          <div className={`font-medium ${remaining.isOverdue ? 'text-red-600' : 'text-blue-600'}`}>
                            {remaining.text}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(order.expected_delivery_date).toLocaleDateString()}
                          </div>
                        </div>
                      );
                    })() : (
                      <div className="text-sm text-muted-foreground">
                        No date set
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {order.customer ? (
                      <div>
                        <div className="font-medium">
                          {order.customer.first_name} {order.customer.last_name}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {order.customer.email}
                        </div>
                        {order.customer_type_id && (
                          <div className="text-xs text-muted-foreground mt-1">
                            {customerTypesResponse.find(type => type.id === order.customer_type_id)?.name || 'Unknown Type'}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">No customer info</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {order.association?.name || `Association ID: ${order.association_id}`}
                  </TableCell>
                  <TableCell>
                    {order.requested_documents && order.requested_documents.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {(() => {
                          const uniqueDocs: { id: number; name: string }[] = [];
                          const seen = new Set<number>();
                          order.requested_documents!.forEach((doc) => {
                            const docTypeId = doc.document?.document_type_id ?? doc.document_id;
                            if (docTypeId && !seen.has(docTypeId)) {
                              seen.add(docTypeId);
                              uniqueDocs.push({ id: docTypeId, name: doc.document?.name || doc.name });
                            }
                          });
                          return uniqueDocs.map((d) => {
                            const color = DOCUMENT_TYPE_COLORS[d.id % DOCUMENT_TYPE_COLORS.length];
                            return (
                              <span
                                key={d.id}
                                className="px-2 py-0.5 rounded-full text-xs font-semibold max-w-[111px] truncate"
                                style={{ backgroundColor: color, color: '#fff' }}
                              >
                                {d.name}
                              </span>
                            );
                          });
                        })()}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={getBadgeVariant(order.status)}>
                      {getStatusText(order.status)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span>${Number(order.total_amount).toFixed(2)}</span>
                      {totalRefunded > 0 && (
                        <span className="text-red-500 text-sm">
                          -${totalRefunded.toFixed(2)} refunded
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {new Date(order.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <Button variant="outline" size="sm" onClick={() => handleViewOrder(order.id)}>
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    ) : (
      <div className="text-center py-8">
        <h3 className="text-lg font-medium">No orders found</h3>
        <p className="text-sm text-muted-foreground mb-4">
          {isLoadingOrders ? "Loading orders..." : "Try a different search term or filter"}
        </p>
      </div>
    )
  );

  const Pagination = () => {
    if (!ordersResponse) return null;

    const totalPages = ordersResponse.totalPages;
    const canGoPrevious = currentPage > 1;
    const canGoNext = currentPage < totalPages;

    return (
      <div className="flex items-center justify-between px-2 py-4">
        <div className="flex-1 text-sm text-muted-foreground">
          Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, ordersResponse.total)} of {ordersResponse.total} results
        </div>
        <div className="flex items-center space-x-6 lg:space-x-8">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium">Rows per page</p>
            <Select
              value={pageSize.toString()}
              onValueChange={(value) => {
                setPageSize(Number(value));
                setCurrentPage(1); // Reset to first page when changing page size
              }}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue placeholder={pageSize} />
              </SelectTrigger>
              <SelectContent side="top">
                {[10, 20, 30, 40, 50].map((size) => (
                  <SelectItem key={size} value={size.toString()}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex w-[100px] items-center justify-center text-sm font-medium">
            Page {currentPage} of {totalPages}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              className="hidden h-8 w-8 p-0 lg:flex"
              onClick={() => setCurrentPage(1)}
              disabled={!canGoPrevious}
            >
              <span className="sr-only">Go to first page</span>
              <ChevronLeft className="h-4 w-4" />
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={!canGoPrevious}
            >
              <span className="sr-only">Go to previous page</span>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={!canGoNext}
            >
              <span className="sr-only">Go to next page</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="hidden h-8 w-8 p-0 lg:flex"
              onClick={() => setCurrentPage(totalPages)}
              disabled={!canGoNext}
            >
              <span className="sr-only">Go to last page</span>
              <ChevronRight className="h-4 w-4" />
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <BackofficeLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Orders</h1>
          <p className="text-muted-foreground">
            Manage document requests
          </p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Filter Orders</CardTitle>
                <CardDescription>
                  Search and filter the list of orders
                </CardDescription>
              </div>
              <Button 
                variant="outline" 
                onClick={handleExportCSV}
                disabled={isExporting || orders.length === 0}
              >
                {isExporting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                Export to CSV
              </Button>
            </div>
            <div className="grid grid-cols-1 gap-4 mt-4 md:grid-cols-3">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search by order id, customer name or email..."
                  className="pl-8"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <Select 
                value={associationId} 
                onValueChange={setAssociationId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Filter by association" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Associations</SelectItem>
                  {associations.map((association) => (
                    <SelectItem key={association.id} value={association.id.toString()}>
                      {association.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select 
                value={customerTypeFilter} 
                onValueChange={setCustomerTypeFilter}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Filter by customer type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Customer Types</SelectItem>
                  {customerTypesResponse.map((type: { id: number; name: string }) => (
                    <SelectItem key={type.id} value={type.id.toString()}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {isLoadingOrders || isLoadingAssociations || isLoadingCustomerTypes ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-brand-blue" />
              </div>
            ) : (
              <>
                <Tabs value={selectedTab} onValueChange={v => setSelectedTab(v as 'all' | 'draft' | 'pending' | 'completed' | 'archived')} className="w-full">
                  <TabsList>
                    <TabsTrigger value="all">All</TabsTrigger>
                    <TabsTrigger value="draft">Draft</TabsTrigger>
                    <TabsTrigger value="pending">Pending</TabsTrigger>
                    <TabsTrigger value="completed">Done</TabsTrigger>
                    <TabsTrigger value="archived">Archived</TabsTrigger>
                  </TabsList>
                  <TabsContent value="all">
                    <OrdersTable ordersList={filteredOrders} />
                  </TabsContent>
                  <TabsContent value="draft">
                    <OrdersTable ordersList={filteredOrders} />
                  </TabsContent>
                  <TabsContent value="pending">
                    <OrdersTable ordersList={filteredOrders} />
                  </TabsContent>
                  <TabsContent value="completed">
                    <OrdersTable ordersList={filteredOrders} />
                  </TabsContent>
                  <TabsContent value="archived">
                    <OrdersTable ordersList={filteredOrders} />
                  </TabsContent>
                </Tabs>
                <Pagination />
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </BackofficeLayout>
  );
};

export default Orders;

